import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { 
  Truck, Target, Zap, Key, Fuel, Anchor, Battery, Wrench,
  MapPin, Car, ChevronRight, ChevronLeft, Check, CreditCard,
  Clock, AlertTriangle, Loader2, Navigation, Phone, MessageSquare,
  Star, Award, Crown, Users, Shield, Search, Sparkles, ChevronDown
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import { MapLayerSwitch, MapLayerButtons } from '../../components/map/MapLayerControls';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Provider Rating Badge Component
const ProviderBadge = ({ badge, badgeText }) => {
  if (!badge) return null;
  
  const badgeStyles = {
    top_rated: {
      bg: 'bg-amber-500/20',
      border: 'border-amber-500/50',
      text: 'text-amber-400',
      icon: Crown
    },
    highly_rated: {
      bg: 'bg-blue-500/20',
      border: 'border-blue-500/50',
      text: 'text-blue-400',
      icon: Award
    },
    premium: {
      bg: 'bg-purple-500/20',
      border: 'border-purple-500/50',
      text: 'text-purple-400',
      icon: Shield
    }
  };
  
  const style = badgeStyles[badge] || badgeStyles.highly_rated;
  const Icon = style.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.border} ${style.text} border`}>
      <Icon className="h-3 w-3" />
      {badgeText}
    </span>
  );
};

// Star Rating Display Component
const StarRatingDisplay = ({ rating, count }) => {
  if (!rating) return <span className="text-xs text-slate-500">No ratings yet</span>;
  
  return (
    <div className="flex items-center gap-1">
      <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
      <span className="text-sm font-medium">{rating.toFixed(1)}</span>
      {count > 0 && (
        <span className="text-xs text-slate-400">({count})</span>
      )}
    </div>
  );
};

// Provider Card Component  
const ProviderCard = ({ provider, serviceType }) => {
  const servicePrice = provider.pricing?.[serviceType];
  
  return (
    <Card className="hover:border-slate-600 transition-all" data-testid={`provider-card-${provider.id}`}>
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-medium truncate">{provider.business_name}</h4>
              <ProviderBadge badge={provider.badge} badgeText={provider.badge_text} />
            </div>
            <div className="flex items-center gap-3 mt-1">
              <StarRatingDisplay rating={provider.rating} count={provider.rating_count} />
              <span className="text-xs text-slate-400">
                {provider.total_jobs || 0} jobs completed
              </span>
            </div>
            {provider.description && (
              <p className="text-xs text-slate-400 mt-1 line-clamp-1">{provider.description}</p>
            )}
          </div>
          {servicePrice && (
            <div className="text-right shrink-0">
              <p className="text-lg font-bold text-green-400">${servicePrice}</p>
              <p className="text-xs text-slate-400">service fee</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Service icons mapping
const SERVICE_ICONS = {
  towing: Truck,
  tire_change: Target,
  jump_start: Zap,
  lockout: Key,
  fuel_delivery: Fuel,
  winch_out: Anchor,
  battery_replace: Battery,
  mobile_mechanic: Wrench,
};

// Map click handler component
const LocationMarker = ({ position, setPosition }) => {
  const map = useMap();
  
  useMapEvents({
    click(e) {
      setPosition(e.latlng);
      map.flyTo(e.latlng, map.getZoom());
    },
  });

  return position ? <Marker position={position} /> : null;
};

// Geolocation button component
const LocateButton = ({ onLocate }) => {
  const map = useMap();
  const [locating, setLocating] = useState(false);

  const handleLocate = () => {
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const latlng = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        map.flyTo(latlng, 15);
        onLocate(latlng);
        setLocating(false);
      },
      (err) => {
        toast.error('Could not get your location');
        setLocating(false);
      },
      { enableHighAccuracy: true }
    );
  };

  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      className="absolute top-2 right-2 z-[1000]"
      onClick={handleLocate}
      disabled={locating}
    >
      {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />}
      <span className="ml-2">{locating ? 'Locating...' : 'Use My Location'}</span>
    </Button>
  );
};

export default function RoadsideCustomerPage() {
  const { api, user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  
  // Wizard state
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Service types from API
  const [serviceTypes, setServiceTypes] = useState([]);
  const [platformFee, setPlatformFee] = useState(5);
  
  // Step 1: Service selection
  const [selectedService, setSelectedService] = useState(null);
  
  // Step 2: Location
  const [mapPosition, setMapPosition] = useState(null);
  const [mapLayer, setMapLayer] = useState('dark');
  const [locationAddress, setLocationAddress] = useState('');
  const [locationCity, setLocationCity] = useState('');
  const [locationState, setLocationState] = useState('');
  const [locationZip, setLocationZip] = useState('');
  const [reverseGeocoding, setReverseGeocoding] = useState(false);
  
  // Step 3: Vehicle
  const [savedVehicles, setSavedVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [vehicleYear, setVehicleYear] = useState('');
  const [vehicleColor, setVehicleColor] = useState('');
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [useNewVehicle, setUseNewVehicle] = useState(false);
  const [saveNewVehicle, setSaveNewVehicle] = useState(true);
  
  // Heavy-duty vehicle options
  const [vehicleVin, setVehicleVin] = useState('');
  const [lookingUpVin, setLookingUpVin] = useState(false);
  const [vinDecodeInfo, setVinDecodeInfo] = useState(null);
  const [showHeavyDuty, setShowHeavyDuty] = useState(false);
  const [vehicleCategory, setVehicleCategory] = useState('');
  const [axleConfig, setAxleConfig] = useState('');
  const [sleeperType, setSleeperType] = useState('');
  const [trailerCompatibility, setTrailerCompatibility] = useState([]);
  const [weightClass, setWeightClass] = useState('');
  
  // Heavy-duty options lists
  const vehicleCategories = [
    { value: 'passenger', label: 'Passenger Vehicle' },
    { value: 'pickup', label: 'Pickup Truck' },
    { value: 'straight_truck', label: 'Straight Truck' },
    { value: 'tractor_truck', label: 'Tractor Truck (Semi)' },
    { value: 'trailer', label: 'Trailer' },
    { value: 'bus', label: 'Bus' }
  ];
  
  const axleConfigs = [
    { value: '4x2', label: '4x2 (Single Drive)' },
    { value: '4x4', label: '4x4 (AWD)' },
    { value: '6x2', label: '6x2 (Lift Axle)' },
    { value: '6x4', label: '6x4 (Tandem)' },
    { value: '6x6', label: '6x6 (All-Wheel)' },
    { value: '8x4', label: '8x4 (Quad)' }
  ];
  
  const sleeperTypes = [
    { value: 'day_cab', label: 'Day Cab' },
    { value: 'flat_top', label: 'Flat Top' },
    { value: 'mid_roof', label: 'Mid-Roof' },
    { value: 'raised_roof', label: 'Raised Roof' },
    { value: 'studio_sleeper', label: 'Studio' }
  ];
  
  const trailerTypes = [
    { value: 'dry_van', label: 'Dry Van' },
    { value: 'reefer', label: 'Reefer' },
    { value: 'flatbed', label: 'Flatbed' },
    { value: 'tanker', label: 'Tanker' },
    { value: 'container', label: 'Container' }
  ];
  
  const weightClasses = [
    { value: 'class_1', label: 'Class 1 (≤6K lbs)' },
    { value: 'class_2', label: 'Class 2 (6-10K)' },
    { value: 'class_3', label: 'Class 3 (10-14K)' },
    { value: 'class_4', label: 'Class 4 (14-16K)' },
    { value: 'class_5', label: 'Class 5 (16-19K)' },
    { value: 'class_6', label: 'Class 6 (19-26K)' },
    { value: 'class_7', label: 'Class 7 (26-33K)' },
    { value: 'class_8', label: 'Class 8 (33K+)' }
  ];
  
  // Step 4: Review & Submit
  const [description, setDescription] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  
  // Request result
  const [createdRequest, setCreatedRequest] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);

  // Nearby providers state
  const [nearbyProviders, setNearbyProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  
  // Active ads
  const [activeAds, setActiveAds] = useState([]);

  // Fetch service types on mount
  useEffect(() => {
    fetchServiceTypes();
    fetchSavedVehicles();
    fetchActiveAds();
  }, []);

  const fetchServiceTypes = async () => {
    try {
      const res = await api.get('/roadside/service-types');
      setServiceTypes(res.data.services || []);
      setPlatformFee(res.data.platform_fee || 5);
    } catch (error) {
      console.error('Failed to fetch service types:', error);
    }
  };

  const fetchSavedVehicles = async () => {
    try {
      const res = await api.get('/roadside/vehicles');
      setSavedVehicles(res.data || []);
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
    }
  };

  const fetchActiveAds = async () => {
    try {
      const res = await api.get('/roadside/ads/active', {
        params: { placement: 'banner', audience: 'customers' }
      });
      setActiveAds(res.data || []);
    } catch (error) {
      console.log('No active ads');
    }
  };

  // VIN Lookup function
  const lookupVin = async () => {
    const vin = vehicleVin.trim();
    if (!vin || vin.length < 11) {
      toast.error('Enter at least 11 characters for VIN lookup');
      return;
    }
    
    setLookingUpVin(true);
    setVinDecodeInfo(null);
    
    try {
      const res = await api.get(`/roadside/vehicles/vin-decode/${vin}`);
      const data = res.data;
      
      // Auto-fill form fields
      setVehicleMake(data.make || '');
      setVehicleModel(data.model || '');
      setVehicleYear(data.year?.toString() || '');
      setVehicleVin(data.vin || vin);
      
      // Store decode info for display
      setVinDecodeInfo({
        vehicle_type: data.vehicle_type,
        body_class: data.body_class,
        drive_type: data.drive_type,
        fuel_type: data.fuel_type,
        gvwr: data.gvwr,
        axles: data.axles,
        cab_type: data.cab_type
      });
      
      // Auto-show heavy duty if it's a truck
      const isTruck = data.vehicle_type?.toLowerCase().includes('truck') ||
                      data.body_class?.toLowerCase().includes('truck') ||
                      data.gvwr;
      if (isTruck) {
        setShowHeavyDuty(true);
      }
      
      toast.success(`Found: ${data.year} ${data.make} ${data.model}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decode VIN');
    }
    setLookingUpVin(false);
  };

  const handleAdClick = async (ad) => {
    try {
      await api.post(`/roadside/ads/${ad.id}/click`);
    } catch (error) {
      console.log('Failed to track ad click');
    }
    if (ad.link_url) {
      window.open(ad.link_url, '_blank');
    }
  };

  // Fetch nearby providers when location ZIP is available
  const fetchNearbyProviders = async (zipCode, serviceType = null) => {
    if (!zipCode) return;
    setLoadingProviders(true);
    try {
      let url = `/roadside/nearby-providers?zip_code=${zipCode}`;
      if (serviceType) {
        url += `&service_type=${serviceType}`;
      }
      const res = await api.get(url);
      setNearbyProviders(res.data || []);
    } catch (error) {
      console.error('Failed to fetch nearby providers:', error);
      setNearbyProviders([]);
    }
    setLoadingProviders(false);
  };

  // Fetch providers when ZIP code and service are selected
  useEffect(() => {
    if (locationZip && locationZip.length >= 5 && selectedService) {
      fetchNearbyProviders(locationZip, selectedService.id);
    }
  }, [locationZip, selectedService]);

  // Reverse geocode when map position changes
  useEffect(() => {
    if (mapPosition) {
      reverseGeocode(mapPosition);
    }
  }, [mapPosition]);

  const reverseGeocode = async (latlng) => {
    setReverseGeocoding(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latlng.lat}&lon=${latlng.lng}`
      );
      const data = await res.json();
      if (data.address) {
        const addr = data.address;
        setLocationAddress(
          [addr.house_number, addr.road].filter(Boolean).join(' ') || 
          data.display_name?.split(',')[0] || ''
        );
        setLocationCity(addr.city || addr.town || addr.village || addr.county || '');
        setLocationState(addr.state || '');
        setLocationZip(addr.postcode || '');
      }
    } catch (error) {
      console.error('Reverse geocode failed:', error);
    }
    setReverseGeocoding(false);
  };

  const handleSubmitRequest = async () => {
    setSubmitting(true);
    try {
      // Build vehicle info
      let vehicleInfo = null;
      let vehicleId = null;
      
      if (selectedVehicle && !useNewVehicle) {
        vehicleId = selectedVehicle.id;
      } else {
        vehicleInfo = {
          make: vehicleMake,
          model: vehicleModel,
          year: parseInt(vehicleYear) || new Date().getFullYear(),
          color: vehicleColor,
          license_plate: vehiclePlate,
          // Heavy-duty fields
          vin: vehicleVin || null,
          vehicle_category: vehicleCategory || null,
          axle_config: axleConfig || null,
          sleeper_type: sleeperType || null,
          trailer_compatibility: trailerCompatibility.length > 0 ? trailerCompatibility : null,
          weight_class: weightClass || null
        };
        
        // Save new vehicle if requested
        if (saveNewVehicle && vehicleMake && vehicleModel) {
          try {
            const saveRes = await api.post('/roadside/vehicles', vehicleInfo);
            vehicleId = saveRes.data.vehicle_id;
            vehicleInfo = null;
          } catch (e) {
            console.error('Failed to save vehicle:', e);
          }
        }
      }
      
      const requestData = {
        service_type: selectedService.id,
        vehicle_id: vehicleId,
        vehicle_info: vehicleInfo,
        location: {
          lat: mapPosition.lat,
          lng: mapPosition.lng,
          address: locationAddress,
          city: locationCity,
          state: locationState,
          zip: locationZip
        },
        description: description,
        promo_code: promoCode || null
      };
      
      const res = await api.post('/roadside/requests', requestData);
      setCreatedRequest(res.data);
      setPromoApplied(res.data.promo_applied);
      setShowPaymentDialog(true);
      toast.success('Request created! Complete payment to confirm.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    }
    setSubmitting(false);
  };

  const handlePayment = () => {
    // In production, this would integrate with Stripe/PayPal
    // For now, navigate to a payment flow or simulate
    if (createdRequest) {
      navigate(`/roadside/payment/${createdRequest.request_id}`);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return selectedService !== null;
      case 2:
        return mapPosition !== null && locationCity && locationState;
      case 3:
        if (selectedVehicle && !useNewVehicle) return true;
        return vehicleMake && vehicleModel && vehicleYear && vehicleColor;
      case 4:
        return true;
      default:
        return false;
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6" data-testid="step-indicator">
      {[1, 2, 3, 4].map((s) => (
        <React.Fragment key={s}>
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold transition-all ${
              step === s
                ? 'bg-blue-600 text-white'
                : step > s
                ? 'bg-green-600 text-white'
                : 'bg-slate-700 text-slate-400'
            }`}
          >
            {step > s ? <Check className="h-4 w-4" /> : s}
          </div>
          {s < 4 && (
            <div className={`w-12 h-0.5 ${step > s ? 'bg-green-600' : 'bg-slate-700'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // Step 1: Service Selection
  const renderStep1 = () => (
    <div className="space-y-4" data-testid="step-1-service">
      <h2 className="text-xl font-semibold text-center">What do you need help with?</h2>
      <p className="text-slate-400 text-center text-sm">Select the service you require</p>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        {serviceTypes.map((service) => {
          const IconComponent = SERVICE_ICONS[service.id] || Wrench;
          const isSelected = selectedService?.id === service.id;
          
          return (
            <Card
              key={service.id}
              className={`cursor-pointer transition-all hover:border-blue-500 ${
                isSelected ? 'border-blue-500 bg-blue-500/10' : ''
              }`}
              onClick={() => setSelectedService(service)}
              data-testid={`service-${service.id}`}
            >
              <CardContent className="p-4 flex flex-col items-center text-center gap-2">
                <div className={`p-3 rounded-full ${isSelected ? 'bg-blue-600' : 'bg-slate-800'}`}>
                  <IconComponent className="h-6 w-6 text-white" />
                </div>
                <span className="font-medium text-sm">{service.name}</span>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {selectedService && (
        <Card className="mt-4 bg-slate-800/50">
          <CardContent className="p-4">
            <p className="text-slate-300">{selectedService.description}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Step 2: Location Selection
  const renderStep2 = () => (
    <div className="space-y-4" data-testid="step-2-location">
      <h2 className="text-xl font-semibold text-center">Where are you?</h2>
      <p className="text-slate-400 text-center text-sm">Pin your exact location on the map</p>
      
      <div className="relative h-[300px] rounded-lg overflow-hidden border border-slate-700">
        <MapContainer
          center={mapPosition || [34.0522, -118.2437]} // Default to LA
          zoom={mapPosition ? 15 : 10}
          className="h-full w-full"
        >
          <MapLayerSwitch mapLayer={mapLayer} />
          <LocationMarker position={mapPosition} setPosition={setMapPosition} />
          <LocateButton onLocate={setMapPosition} />
        </MapContainer>
        {/* Map Layer Controls */}
        <div className="absolute top-2 right-2 z-[1000]">
          <MapLayerButtons mapLayer={mapLayer} setMapLayer={setMapLayer} />
        </div>
      </div>
      
      {mapPosition && (
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Street Address</Label>
            <Input
              value={locationAddress}
              onChange={(e) => setLocationAddress(e.target.value)}
              placeholder="123 Main St"
              data-testid="input-address"
            />
          </div>
          <div>
            <Label>City</Label>
            <Input
              value={locationCity}
              onChange={(e) => setLocationCity(e.target.value)}
              placeholder="City"
              data-testid="input-city"
            />
          </div>
          <div>
            <Label>State</Label>
            <Input
              value={locationState}
              onChange={(e) => setLocationState(e.target.value)}
              placeholder="State"
              data-testid="input-state"
            />
          </div>
          <div>
            <Label>ZIP Code</Label>
            <Input
              value={locationZip}
              onChange={(e) => setLocationZip(e.target.value)}
              placeholder="ZIP"
              data-testid="input-zip"
            />
          </div>
        </div>
      )}
      
      {reverseGeocoding && (
        <div className="flex items-center justify-center gap-2 text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Getting address...</span>
        </div>
      )}
      
      {/* Nearby Providers Section */}
      {mapPosition && locationZip && locationZip.length >= 5 && selectedService && (
        <div className="mt-4 pt-4 border-t border-slate-700" data-testid="nearby-providers-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-400" />
              Nearby Providers for {selectedService.name}
            </h3>
            {loadingProviders && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          </div>
          
          {!loadingProviders && nearbyProviders.length === 0 && (
            <Card className="bg-slate-800/30 border-dashed">
              <CardContent className="p-4 text-center">
                <Users className="h-8 w-8 text-slate-600 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No providers available in this area</p>
                <p className="text-xs text-slate-500 mt-1">Try a different location or service</p>
              </CardContent>
            </Card>
          )}
          
          {!loadingProviders && nearbyProviders.length > 0 && (
            <div className="space-y-2">
              {nearbyProviders.slice(0, 3).map((provider) => (
                <ProviderCard 
                  key={provider.id} 
                  provider={provider} 
                  serviceType={selectedService.id}
                />
              ))}
              {nearbyProviders.length > 3 && (
                <p className="text-xs text-slate-400 text-center pt-2">
                  +{nearbyProviders.length - 3} more providers available
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Step 3: Vehicle Selection
  const renderStep3 = () => (
    <div className="space-y-4" data-testid="step-3-vehicle">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Your Vehicle</h2>
          <p className="text-slate-400 text-sm">Select or enter your vehicle details</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/roadside/vehicles')}
          className="text-blue-400 hover:text-blue-300"
          data-testid="manage-vehicles-btn"
        >
          <Car className="h-4 w-4 mr-1" />
          Manage Vehicles
        </Button>
      </div>
      
      {savedVehicles.length > 0 && !useNewVehicle && (
        <div className="space-y-2">
          <Label>Saved Vehicles</Label>
          <div className="grid gap-2">
            {savedVehicles.map((vehicle) => {
              const isTruck = vehicle.vehicle_category === 'tractor_truck' || 
                             vehicle.vehicle_category === 'straight_truck';
              return (
                <Card
                  key={vehicle.id}
                  className={`cursor-pointer transition-all hover:border-blue-500 ${
                    selectedVehicle?.id === vehicle.id ? 'border-blue-500 bg-blue-500/10' : ''
                  }`}
                  onClick={() => setSelectedVehicle(vehicle)}
                  data-testid={`vehicle-${vehicle.id}`}
                >
                  <CardContent className="p-3 flex items-center gap-3">
                    {isTruck ? (
                      <Truck className="h-5 w-5 text-orange-400" />
                    ) : (
                      <Car className="h-5 w-5 text-slate-400" />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">
                          {vehicle.year} {vehicle.make} {vehicle.model}
                        </p>
                        {vehicle.vehicle_category && vehicle.vehicle_category !== 'passenger' && (
                          <Badge variant="secondary" className="text-xs bg-orange-500/20 text-orange-300">
                            {vehicleCategories.find(c => c.value === vehicle.vehicle_category)?.label || vehicle.vehicle_category}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-400">
                        {vehicle.color}
                        {vehicle.license_plate && ` • ${vehicle.license_plate}`}
                        {vehicle.axle_config && ` • ${vehicle.axle_config}`}
                        {vehicle.weight_class && ` • Class ${vehicle.weight_class.replace('class_', '')}`}
                      </p>
                    </div>
                    {selectedVehicle?.id === vehicle.id && (
                      <Check className="h-5 w-5 text-green-500" />
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <Button
            variant="outline"
            className="w-full mt-2"
            onClick={() => {
              setUseNewVehicle(true);
              setSelectedVehicle(null);
            }}
          >
            + Add New Vehicle
          </Button>
        </div>
      )}
      
      {(savedVehicles.length === 0 || useNewVehicle) && (
        <div className="space-y-3">
          {savedVehicles.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setUseNewVehicle(false)}
            >
              ← Back to saved vehicles
            </Button>
          )}
          
          {/* VIN Lookup Section */}
          <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg space-y-2">
            <div className="flex items-center gap-2 text-blue-400 text-sm font-medium">
              <Sparkles className="h-4 w-4" />
              Quick Fill with VIN
            </div>
            <div className="flex gap-2">
              <Input
                value={vehicleVin}
                onChange={(e) => setVehicleVin(e.target.value.toUpperCase())}
                placeholder="Enter VIN to auto-fill"
                maxLength={17}
                className="font-mono uppercase"
                data-testid="input-vin-wizard"
              />
              <Button 
                type="button"
                onClick={lookupVin}
                disabled={lookingUpVin || vehicleVin.length < 11}
                className="shrink-0"
                data-testid="lookup-vin-wizard-btn"
              >
                {lookingUpVin ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
          
          {/* VIN Decode Result */}
          {vinDecodeInfo && (
            <div className="p-2 bg-green-500/10 border border-green-500/20 rounded text-xs text-green-400">
              <Check className="h-3 w-3 inline mr-1" />
              {vinDecodeInfo.vehicle_type && `${vinDecodeInfo.vehicle_type} • `}
              {vinDecodeInfo.body_class && `${vinDecodeInfo.body_class} • `}
              {vinDecodeInfo.fuel_type && vinDecodeInfo.fuel_type}
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Make *</Label>
              <Input
                value={vehicleMake}
                onChange={(e) => setVehicleMake(e.target.value)}
                placeholder="Toyota"
                data-testid="input-make"
              />
            </div>
            <div>
              <Label>Model *</Label>
              <Input
                value={vehicleModel}
                onChange={(e) => setVehicleModel(e.target.value)}
                placeholder="Camry"
                data-testid="input-model"
              />
            </div>
            <div>
              <Label>Year *</Label>
              <Input
                type="number"
                value={vehicleYear}
                onChange={(e) => setVehicleYear(e.target.value)}
                placeholder="2020"
                data-testid="input-year"
              />
            </div>
            <div>
              <Label>Color *</Label>
              <Input
                value={vehicleColor}
                onChange={(e) => setVehicleColor(e.target.value)}
                placeholder="Silver"
                data-testid="input-color"
              />
            </div>
            <div className="col-span-2">
              <Label>License Plate (optional)</Label>
              <Input
                value={vehiclePlate}
                onChange={(e) => setVehiclePlate(e.target.value.toUpperCase())}
                placeholder="ABC123"
                className="uppercase"
                data-testid="input-plate"
              />
            </div>
          </div>
          
          {/* Heavy-Duty Truck Options */}
          <Collapsible open={showHeavyDuty} onOpenChange={setShowHeavyDuty}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-2 h-auto border border-slate-700 hover:bg-slate-800"
                data-testid="heavy-duty-toggle-wizard"
              >
                <div className="flex items-center gap-2 text-sm">
                  <Truck className="h-4 w-4 text-orange-400" />
                  <span>Heavy-Duty Options</span>
                  {(vehicleCategory || axleConfig || weightClass) && (
                    <Badge variant="secondary" className="text-xs">Set</Badge>
                  )}
                </div>
                <ChevronDown className={`h-4 w-4 transition-transform ${showHeavyDuty ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 pt-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Category</Label>
                  <Select value={vehicleCategory} onValueChange={setVehicleCategory}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleCategories.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Weight Class</Label>
                  <Select value={weightClass} onValueChange={setWeightClass}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {weightClasses.map(w => (
                        <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Axle Config</Label>
                  <Select value={axleConfig} onValueChange={setAxleConfig}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {axleConfigs.map(a => (
                        <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Sleeper Type</Label>
                  <Select value={sleeperType} onValueChange={setSleeperType}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {sleeperTypes.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label className="text-xs">Trailer Compatibility</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {trailerTypes.map(t => (
                    <label key={t.value} className="flex items-center gap-1 text-xs cursor-pointer">
                      <Checkbox
                        checked={trailerCompatibility.includes(t.value)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setTrailerCompatibility([...trailerCompatibility, t.value]);
                          } else {
                            setTrailerCompatibility(trailerCompatibility.filter(x => x !== t.value));
                          }
                        }}
                      />
                      <span className="text-slate-300">{t.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="save-vehicle"
              checked={saveNewVehicle}
              onChange={(e) => setSaveNewVehicle(e.target.checked)}
              className="rounded"
            />
            <Label htmlFor="save-vehicle" className="text-sm cursor-pointer">
              Save this vehicle for future requests
            </Label>
          </div>
        </div>
      )}
    </div>
  );

  // Step 4: Review & Submit
  const renderStep4 = () => {
    const vehicle = selectedVehicle && !useNewVehicle
      ? selectedVehicle
      : { make: vehicleMake, model: vehicleModel, year: vehicleYear, color: vehicleColor };
    
    return (
      <div className="space-y-4" data-testid="step-4-review">
        <h2 className="text-xl font-semibold text-center">Review Your Request</h2>
        
        <div className="space-y-3">
          {/* Service */}
          <Card className="bg-slate-800/50">
            <CardContent className="p-4 flex items-center gap-3">
              {selectedService && (
                <>
                  {React.createElement(SERVICE_ICONS[selectedService.id] || Wrench, {
                    className: 'h-5 w-5 text-blue-400'
                  })}
                  <div>
                    <p className="font-medium">{selectedService.name}</p>
                    <p className="text-sm text-slate-400">Service Type</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
          
          {/* Location */}
          <Card className="bg-slate-800/50">
            <CardContent className="p-4 flex items-center gap-3">
              <MapPin className="h-5 w-5 text-green-400" />
              <div>
                <p className="font-medium">{locationAddress || 'Location pinned'}</p>
                <p className="text-sm text-slate-400">{locationCity}, {locationState} {locationZip}</p>
              </div>
            </CardContent>
          </Card>
          
          {/* Vehicle */}
          <Card className="bg-slate-800/50">
            <CardContent className="p-4 flex items-center gap-3">
              <Car className="h-5 w-5 text-amber-400" />
              <div>
                <p className="font-medium">{vehicle.year} {vehicle.make} {vehicle.model}</p>
                <p className="text-sm text-slate-400">{vehicle.color}</p>
              </div>
            </CardContent>
          </Card>
          
          {/* Description */}
          <div>
            <Label>Additional Details (optional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Any specific details about your situation..."
              className="h-20"
              data-testid="input-description"
            />
          </div>
          
          {/* Promo Code */}
          <div>
            <Label>Promo Code</Label>
            <Input
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              placeholder="Enter code"
              data-testid="input-promo"
            />
          </div>
          
          {/* Fee Summary */}
          <Card className="border-blue-500/50 bg-blue-500/5">
            <CardContent className="p-4">
              <div className="flex justify-between items-center">
                <span>Platform Fee</span>
                <span className="font-bold text-lg">${platformFee.toFixed(2)}</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Pay once to connect with nearby providers
              </p>
            </CardContent>
          </Card>
          
          {/* Nearby Providers Preview */}
          {nearbyProviders.length > 0 && (
            <div className="mt-4" data-testid="review-providers-section">
              <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
                <Users className="h-4 w-4 text-green-400" />
                {nearbyProviders.length} Provider{nearbyProviders.length > 1 ? 's' : ''} Available
              </h3>
              <div className="space-y-2">
                {nearbyProviders.slice(0, 2).map((provider) => (
                  <div 
                    key={provider.id}
                    className="flex items-center justify-between bg-slate-800/50 rounded-lg p-2"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{provider.business_name}</span>
                      <ProviderBadge badge={provider.badge} badgeText={provider.badge_text} />
                    </div>
                    <StarRatingDisplay rating={provider.rating} count={provider.rating_count} />
                  </div>
                ))}
              </div>
              {nearbyProviders.length > 2 && (
                <p className="text-xs text-slate-400 text-center mt-2">
                  +{nearbyProviders.length - 2} more ready to help
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 md:p-6" data-testid="roadside-customer-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl md:text-3xl font-bold">
            <span className="text-blue-400">Roadside</span> Rescue
          </h1>
          <p className="text-slate-400 mt-1">Fast help when you need it most</p>
          <Button 
            variant="ghost" 
            size="sm" 
            className="mt-2 text-slate-400 hover:text-white"
            onClick={() => navigate('/roadside/history')}
            data-testid="view-history-btn"
          >
            <Clock className="h-4 w-4 mr-1" />
            View Request History
          </Button>
        </div>
        
        {/* Step Indicator */}
        {renderStepIndicator()}
        
        {/* Ad Banner */}
        {activeAds.length > 0 && (
          <div className="mb-4">
            {activeAds.slice(0, 1).map((ad) => (
              <Card 
                key={ad.id}
                className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-blue-700/30 cursor-pointer hover:border-blue-600/50 transition-colors overflow-hidden"
                onClick={() => handleAdClick(ad)}
                data-testid={`ad-banner-${ad.id}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-center gap-4">
                    {ad.image_url && (
                      <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0 hidden sm:block">
                        <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-500/20 text-blue-400 text-xs">Sponsored</Badge>
                        <p className="font-medium text-sm truncate">{ad.title}</p>
                      </div>
                      <p className="text-xs text-slate-400 line-clamp-1 mt-0.5">{ad.description}</p>
                    </div>
                    {ad.link_url && (
                      <Button variant="ghost" size="sm" className="text-blue-400 shrink-0">
                        Learn More
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {/* Step Content */}
        <Card className="mb-6">
          <CardContent className="p-6">
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
          </CardContent>
        </Card>
        
        {/* Navigation Buttons */}
        <div className="flex justify-between gap-4">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              data-testid="btn-back"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          )}
          
          <div className="flex-1" />
          
          {step < 4 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              data-testid="btn-next"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmitRequest}
              disabled={!canProceed() || submitting}
              className="bg-green-600 hover:bg-green-500"
              data-testid="btn-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating Request...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Continue to Payment (${platformFee.toFixed(2)})
                </>
              )}
            </Button>
          )}
        </div>
        
        {/* Payment Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Complete Payment</DialogTitle>
              <DialogDescription>
                Pay the ${createdRequest?.platform_fee?.toFixed(2) || platformFee.toFixed(2)} platform fee to connect with nearby providers.
              </DialogDescription>
            </DialogHeader>
            
            {promoApplied && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-sm">
                <span className="text-green-400">✓ Promo code applied!</span>
                <span className="ml-2">-${promoApplied.discount.toFixed(2)}</span>
              </div>
            )}
            
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setShowPaymentDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePayment}
                className="bg-blue-600 hover:bg-blue-500"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Pay with Card
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
